import React from 'react';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import BackLink from 'src/components/ui/BackLink';
import Icon from 'src/components/ui/Icon';
import Menu, { MenuItem } from 'src/components/ui/Menu';
import ReportModal from 'src/components/ui/ReportModal';
import Typography from 'src/components/ui/Typography';

import useAppRouter from 'src/hooks/useAppRouter';
import useModalOpenState from 'src/hooks/useModalOpenState';
import useUser from 'src/hooks/useUser';

import type { AdQuery, Medium } from 'src/types/__generated__/graphql';

import InteractionsCTAs from 'src/components/news/InteractionsCTAs';
import MoreIcon from 'src/components/news/MoreIcon';
import TooltipButton from 'src/components/ui/TooltipButton';
import { colours } from 'src/constants/colours';

interface Props {
    ad: NonNullable<AdQuery['ad']>;
}

const AdHeader: React.FC<Props> = ({ ad }) => {
    const { t } = useTranslation(['ad', 'common']);

    const router = useAppRouter();
    const currentUser = useUser();

    const { props: reportModalProps, open: openReportModal } = useModalOpenState();

    const moreButton =
        !currentUser || ad.owner?.user?.id === currentUser.id ? undefined : (
            <>
                <Box mx={1}>
                    <Menu
                        id="more-menu"
                        items={[
                            <MenuItem key={`report-${ad.id}`} onClick={openReportModal}>
                                <Icon name="Alert" size="1rem" />
                                <Box ml={1}>
                                    <Typography variant="h6">{t('report')}</Typography>
                                </Box>
                            </MenuItem>,
                        ]}
                    >
                        <TooltipButton
                            title={t('common:tooltips.plus')}
                            ButtonComponent={MoreIcon}
                            buttonProps={{
                                ariaLabel: 'settings',
                                ariaControls: `ad-${ad.id}`,
                                ariaHasPopup: 'true',
                                size: 'large',
                                iconProps: { color: colours.primary.main },
                            }}
                        />
                    </Menu>
                </Box>
                {reportModalProps.open && <ReportModal adId={ad.id} {...reportModalProps} />}
            </>
        );

    return (
        <Box py={2} display="flex" alignItems="center" justifyContent="space-between">
            <BackLink onClick={() => router.back()}>{t('common:back')}</BackLink>
            <InteractionsCTAs
                ad={{ ...ad, media: ad.media as Medium[] }}
                renderAfter={moreButton}
                iconProps={{ size: '1.5rem' }}
            />
        </Box>
    );
};

export default AdHeader;
